import { colors } from '../../../../constants/FlowBuilder';
import ConnectorLine from '../../connector-line/ConnectorLine';
import { drawCanvasImage } from '../../helpers';
import { CanvasConnectorIcon, CanvasElementConfig } from '../../TypeDefs';

/**
   * @method drawConnectorIcon
   * @description Draw connector icon
   * @param ctx canvas 2d context,
   * @param icon Icon
   * @param xCoordinate Connector X coordinate
   * @param yCoordinate Connector Y coordinate
   * @param inConnector Connector in coordinate
   */
export const drawConnectorIcon = ({
  ctx,
  icon,
  inConnector,
  xCoordinate,
  yCoordinate,
  stepConnector,
  config
}: {
  ctx: CanvasRenderingContext2D,
  icon: CanvasConnectorIcon,
  xCoordinate: number,
  yCoordinate: number,
  inConnector: boolean,
  stepConnector: boolean,
  config: CanvasElementConfig
}) => {
  const iconSize = Math.max(icon.width, icon.height);
  const size = iconSize + (stepConnector ? (config.padding / 2) : config.padding);
  if (inConnector) {
    config.inConnectorRadius = size / 2;
  } else {
    config.outConnectorRadius = size / 2;
  }
  if (stepConnector) {
    ctx.shadowBlur = 10;
    ctx.shadowOffsetY = 5;
    ctx.shadowColor = 'rgba(0,0,0,0.2)';
  }
  ctx.beginPath();
  ctx.arc(
    xCoordinate,
    yCoordinate,
    size / 2,
    0,
    2 * Math.PI,
  );
  ctx.fillStyle = stepConnector ? colors.white : config.btnGroupBackgroundColor;
  ctx.fill();
  if (stepConnector) {
    ctx.shadowBlur = 0;
    ctx.shadowOffsetY = 0;
  }
  drawCanvasImage(ctx, icon.image, xCoordinate - icon.width / 2, yCoordinate - icon.height / 2, icon.width, icon.height);
};

/**
   * @method drawIncomingHorizontalConnector
   * @description Draw incoming horizontal connector
   * @param ctx canvas 2d context
   */
export const drawIncomingHorizontalConnector = ({
  ctx,
  incomingIcon,
  incomingHX,
  incomingHY,
  config,
  isRoot,
  incomingConnectors,
  isSelected,
}: {
  ctx: CanvasRenderingContext2D,
  incomingIcon?: CanvasConnectorIcon,
  incomingHX: number,
  incomingHY: number,
  config: CanvasElementConfig,
  isRoot: boolean,
  incomingConnectors: ConnectorLine[],
  isSelected: boolean,
}) => {
  if (incomingIcon) {
    drawConnectorIcon({
      ctx,
      icon: incomingIcon,
      config,
      inConnector: true,
      xCoordinate: incomingHX,
      yCoordinate: incomingHY,
      stepConnector: false
    });
    config.inConnectorRadius = Math.max(incomingIcon.height, incomingIcon.width) / 2;
    return;
  }
  if (isRoot || config.inConnectorRadius === 0 || incomingConnectors.length > 0) {
    return;
  }
  if (config.connectorOutlined) {
    ctx.strokeStyle = isSelected
      ? config.selectedIncomingFillColor
      : config.incomingFillColor;
  } else {
    ctx.fillStyle = isSelected
      ? config.selectedIncomingFillColor
      : config.incomingFillColor;
  }
  ctx.beginPath();
  ctx.arc(
    incomingHX,
    incomingHY,
    config.outConnectorRadius,
    0,
    2 * Math.PI,
  );
  ctx.fill();
  if (config.connectorOutlined) {
    ctx.stroke();
  }
};

/**
 * @method drawOutgoingHorizontalConnector
 * @description Draw outgoing horizontal connector
 * @param ctx canvas 2d context
 */
export const drawOutgoingHorizontalConnector = ({
  ctx,
  outgoingIcon,
  outgoingHX,
  outgoingHY,
  config,
  isSelected,
}: {
  ctx: CanvasRenderingContext2D,
  outgoingIcon?: CanvasConnectorIcon,
  outgoingHX: number,
  outgoingHY: number,
  config: CanvasElementConfig,
  isSelected: boolean,
}) => {
  if (outgoingIcon) {
    drawConnectorIcon({
      ctx,
      icon: outgoingIcon,
      config,
      inConnector: false,
      xCoordinate: outgoingHX,
      yCoordinate: outgoingHY,
      stepConnector: false
    });
    config.outConnectorRadius = Math.max(outgoingIcon.height, outgoingIcon.width) / 2 + config.padding;
    return;
  }
  if (config.outConnectorRadius === 0) {
    return;
  }
  if (config.connectorOutlined) {
    ctx.strokeStyle = isSelected
      ? config.selectedOutgoingFillColor
      : config.outgoingFillColor;
  } else {
    ctx.fillStyle = isSelected
      ? config.selectedOutgoingFillColor
      : config.outgoingFillColor;
  }
  ctx.beginPath();
  ctx.arc(
    outgoingHX,
    outgoingHY,
    config.outConnectorRadius,
    0,
    2 * Math.PI,
  );
  ctx.fill();
  if (config.connectorOutlined) {
    ctx.stroke();
  }
};

/**
 * @method drawIncomingVerticalConnector
 * @description Draw incoming vertical connector
 * @param ctx canvas 2d context
 */
export const drawIncomingVerticalConnector = ({
  ctx,
  incomingIcon,
  incomingVX,
  incomingVY,
  config,
  isRoot,
  incomingConnectors,
  isSelected,
}: {
  ctx: CanvasRenderingContext2D,
  incomingIcon?: CanvasConnectorIcon,
  incomingVX: number,
  incomingVY: number,
  config: CanvasElementConfig,
  isRoot: boolean,
  incomingConnectors: ConnectorLine[],
  isSelected: boolean,
}) => {
  if (incomingIcon) {
    drawConnectorIcon({
      ctx,
      config,
      icon: incomingIcon,
      inConnector: true,
      stepConnector: false,
      xCoordinate: incomingVX,
      yCoordinate: incomingVY,
    });
    return;
  }
  if (isRoot || config.inConnectorRadius === 0 || incomingConnectors.length > 0) {
    return;
  }
  if (config.connectorOutlined) {
    ctx.strokeStyle = isSelected
      ? config.selectedIncomingFillColor
      : config.incomingFillColor;
  } else {
    ctx.fillStyle = isSelected
      ? config.selectedIncomingFillColor
      : config.incomingFillColor;
  }
  ctx.beginPath();
  ctx.arc(
    incomingVX,
    incomingVY,
    config.outConnectorRadius,
    0,
    2 * Math.PI,
  );
  ctx.fill();
  if (config.connectorOutlined) {
    ctx.stroke();
  }
};

/**
 * @method drawOutgoingVerticalConnector
 * @description Draw outgoing vertical connector
 * @param ctx canvas 2d context
 */
export const drawOutgoingVerticalConnector = ({
  ctx,
  outgoingIcon,
  outgoingVX,
  outgoingVY,
  config,
  isSelected,
}: {
  ctx: CanvasRenderingContext2D,
  outgoingIcon?: CanvasConnectorIcon,
  outgoingVX: number,
  outgoingVY: number,
  config: CanvasElementConfig,
  isSelected: boolean,
}) => {
  if (outgoingIcon) {
    drawConnectorIcon({
      ctx,
      icon: outgoingIcon,
      inConnector: false,
      stepConnector: false,
      xCoordinate: outgoingVX,
      yCoordinate: outgoingVY,
      config,
    });
    return;
  }
  if (config.outConnectorRadius === 0) {
    return;
  }
  if (config.connectorOutlined) {
    ctx.strokeStyle = isSelected
      ? config.selectedOutgoingFillColor
      : config.outgoingFillColor;
  } else {
    ctx.fillStyle = isSelected
      ? config.selectedOutgoingFillColor
      : config.outgoingFillColor;
  }
  ctx.beginPath();
  ctx.arc(
    outgoingVX,
    outgoingVY,
    config.outConnectorRadius,
    0,
    2 * Math.PI,
  );
  ctx.fill();
  if (config.connectorOutlined) {
    ctx.stroke();
  }
};
